import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Group,
  Image,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconTrash } from '@tabler/icons-react';

import { BasicButton } from '@components/Button';
import { PageTitle } from '@components/shared';
import entityColor from '@util/entityColor';
import entityIcon from '@util/entityIcon';
import { calendarDateValue } from '@util/formatters';
import { toErrorMessage } from '@util/toErrorMessage';

const STAT_VARIETIES = [
  { value: 'bodyweight', label: 'bodyweight', unit: 'lb' },
  { value: 'height', label: 'height', unit: 'in' },
  { value: 'wingspan', label: 'wingspan', unit: 'in' },
  { value: 'standing_reach', label: 'standing reach', unit: 'in' },
  { value: 'vertical_leap', label: 'vertical leap', unit: 'in' },
  { value: 'broad_jump', label: 'broad jump', unit: 'in' },
  { value: 'sprint_10', label: 'sprint 10', unit: 'sec' },
  { value: 'sprint_20', label: 'sprint 20', unit: 'sec' },
  { value: 'sprint_40', label: 'sprint 40', unit: 'sec' },
  { value: 'pro_agility', label: 'pro agility', unit: 'sec' },
  { value: 'shuttle_60', label: 'shuttle 60', unit: 'sec' },
  { value: 'body_fat_pct', label: 'body fat pct', unit: '%' },
  { value: 'resting_hr', label: 'resting hr', unit: 'bpm' },
];

const DAY_KIND_OPTIONS = [
  { value: '1', label: 'Mandatory lift' },
  { value: '2', label: 'Mandatory rest' },
  { value: '3', label: 'Optional recovery' },
];

const DEFAULT_PATTERN = ['1', '2', '1', '2'];

const WEEKDAY_OPTIONS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatLabel(value) {
  if (!value) return '—';
  return String(value).replace(/_/g, ' ');
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatStatTakenAt(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatStatValue(stat) {
  if (!stat || stat.value == null || stat.value === '') return '—';
  const number = Number(stat.value);
  const unit = stat.unit || '';
  if (!Number.isFinite(number)) return `${stat.value}${unit ? ` ${unit}` : ''}`;
  return `${number}${unit ? ` ${unit}` : ''}`;
}

function formatSet(set) {
  const bits = [];
  if (set.prescribed_weight != null) bits.push(`${Number(set.prescribed_weight)} lb`);
  if (set.prescribed_reps != null) bits.push(`${set.prescribed_reps} reps`);
  if (set.prescribed_how) bits.push(set.prescribed_how);
  if (set.prescribed_rest_seconds) bits.push(`${set.prescribed_rest_seconds}s rest`);
  if (set.estimated_rpe != null) bits.push(`RPE ${set.estimated_rpe}`);
  return bits.join(' · ') || '—';
}

function slotStatusColor(status) {
  if (status === 'completed') return 'teal';
  if (status === 'missed') return 'red';
  if (status === 'skipped') return 'gray';
  return 'blue';
}

function blockStatusColor(status) {
  if (status === 'active') return 'teal';
  if (status === 'inactive') return 'gray';
  if (status === 'deleted') return 'red';
  return 'blue';
}

function formatOverlayWhen(event) {
  if (event?.on_date) return formatDate(event.on_date);
  if (event?.weekday != null && event.weekday !== '') {
    const label = WEEKDAY_OPTIONS.find((item) => item.value === String(event.weekday))?.label;
    return label ? `Every ${label}` : `Weekday ${event.weekday}`;
  }
  return '—';
}

export default function AdminAthleteDetail() {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [sport, setSport] = useState('');
  const [position, setPosition] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [addingStat, setAddingStat] = useState(false);
  const [statVariety, setStatVariety] = useState('bodyweight');
  const [statValue, setStatValue] = useState('');
  const [statUnit, setStatUnit] = useState('lb');
  const [statSaving, setStatSaving] = useState(false);
  const [confirmStatDeleteId, setConfirmStatDeleteId] = useState(null);

  const [addingMax, setAddingMax] = useState(false);
  const [maxExerciseId, setMaxExerciseId] = useState(null);
  const [maxValue, setMaxValue] = useState('');
  const [maxSaving, setMaxSaving] = useState(false);
  const [confirmMaxDeleteId, setConfirmMaxDeleteId] = useState(null);

  const [weekAvailable, setWeekAvailable] = useState(() => Array(7).fill(true));
  const [availabilitySaving, setAvailabilitySaving] = useState(false);

  const [addingOverlay, setAddingOverlay] = useState(false);
  const [editingOverlayId, setEditingOverlayId] = useState(null);
  const [overlayName, setOverlayName] = useState('');
  const [overlayMode, setOverlayMode] = useState('one_off');
  const [overlayDate, setOverlayDate] = useState(null);
  const [overlayWeekday, setOverlayWeekday] = useState('3');
  const [overlayBlocks, setOverlayBlocks] = useState(true);
  const [overlayWorkoutId, setOverlayWorkoutId] = useState(null);
  const [overlaySaving, setOverlaySaving] = useState(false);
  const [confirmOverlayDeleteId, setConfirmOverlayDeleteId] = useState(null);

  const [creatingBlock, setCreatingBlock] = useState(false);
  const [blockName, setBlockName] = useState('');
  const [blockStart, setBlockStart] = useState(null);
  const [blockEnd, setBlockEnd] = useState(null);
  const [blockPattern, setBlockPattern] = useState(DEFAULT_PATTERN);
  const [blockSaving, setBlockSaving] = useState(false);
  const [blockStatusBusy, setBlockStatusBusy] = useState(false);
  const [confirmBlockDeleteId, setConfirmBlockDeleteId] = useState(null);
  const [confirmBlockInactivateId, setConfirmBlockInactivateId] = useState(null);

  const athleteQuery = useQuery({
    queryKey: ['admin-athlete', athleteId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(`/athletes/${athleteId}/`, { signal });
      return data.response?.[0];
    },
    enabled: Boolean(athleteId),
  });

  const blocksQuery = useQuery({
    queryKey: ['admin-training-blocks', athleteId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.post(
        '/retrieve-training-blocks/',
        { athlete_id: athleteId },
        { signal },
      );
      return data.response?.[0] || { historical: [], current: [], upcoming: [] };
    },
    enabled: Boolean(athleteId),
  });

  const availabilityQuery = useQuery({
    queryKey: ['admin-athlete-availability', athleteId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.post(
        '/retrieve-athlete-availability/',
        { athlete_id: athleteId },
        { signal },
      );
      return data.response?.[0];
    },
    enabled: Boolean(athleteId),
  });

  const overlaysQuery = useQuery({
    queryKey: ['admin-athlete-calendar-events', athleteId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.post(
        '/retrieve-athlete-calendar-events/',
        { athlete_id: athleteId },
        { signal },
      );
      return data.response?.[0]?.data || [];
    },
    enabled: Boolean(athleteId),
  });

  const workoutsQuery = useQuery({
    queryKey: ['admin-workouts'],
    queryFn: async ({ signal }) => {
      const { data } = await axios.post(
        '/retrieve-workouts/',
        { page: 0, page_size: 100 },
        { signal },
      );
      return data.response?.[0]?.data || [];
    },
  });

  const exercisesPickerQuery = useQuery({
    queryKey: ['admin-exercises-picker'],
    queryFn: async ({ signal }) => {
      const { data } = await axios.post(
        '/retrieve-exercises/',
        { page: 0, page_size: 500, status: 2 },
        { signal },
      );
      return data.response?.[0]?.data || [];
    },
  });

  const awardsQuery = useQuery({
    queryKey: ['admin-athlete-achievement-awards', athleteId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(
        `/athletes/${athleteId}/achievement-awards/`,
        { signal },
      );
      return Array.isArray(data.response) ? data.response : [];
    },
    enabled: Boolean(athleteId),
  });

  const athlete = athleteQuery.data;
  const stats = Array.isArray(athlete?.stats) ? athlete.stats : [];
  const liftMaxes = Array.isArray(athlete?.exercise_maxes)
    ? athlete.exercise_maxes
    : [];
  const awards = awardsQuery.data || [];
  const focusBlock = athlete?.current_block;
  const focusSlot = athlete?.focus_slot;
  const slots = focusBlock?.slots || [];
  const exercises = focusSlot?.workout?.exercises || [];
  const historicalBlocks = blocksQuery.data?.historical || [];
  const currentBlocks = blocksQuery.data?.current || [];
  const upcomingBlocks = blocksQuery.data?.upcoming || [];
  const overlays = overlaysQuery.data || [];
  const libraryWorkouts = workoutsQuery.data || [];

  const exerciseOptions = useMemo(
    () =>
      (exercisesPickerQuery.data || []).map((exercise) => ({
        value: String(exercise.id),
        label: exercise.name || 'Exercise',
      })),
    [exercisesPickerQuery.data],
  );

  useEffect(() => {
    if (!athlete) return;
    setFirstName(athlete.first_name || '');
    setLastName(athlete.last_name || '');
    setEmail(athlete.email || '');
    setSport(athlete.sport || '');
    setPosition(athlete.position || '');
  }, [athlete]);

  useEffect(() => {
    const match = STAT_VARIETIES.find((item) => item.value === statVariety);
    if (match) setStatUnit(match.unit);
  }, [statVariety]);

  useEffect(() => {
    const days = availabilityQuery.data?.week_days || [];
    if (!days.length) return;
    const next = Array(7).fill(true);
    days.forEach((day) => {
      const weekday = Number(day.weekday);
      if (weekday >= 0 && weekday <= 6) next[weekday] = !!day.available;
    });
    setWeekAvailable(next);
  }, [availabilityQuery.data]);

  const varietyOptions = useMemo(
    () =>
      STAT_VARIETIES.map((item) => ({
        value: item.value,
        label: item.label,
      })),
    [],
  );

  const workoutOptions = useMemo(
    () =>
      libraryWorkouts.map((workout) => ({
        value: workout.id,
        label: workout.name || workout.id,
      })),
    [libraryWorkouts],
  );

  function refreshAthlete(updated) {
    if (updated) {
      queryClient.setQueryData(['admin-athlete', athleteId], updated);
    } else {
      queryClient.invalidateQueries({ queryKey: ['admin-athlete', athleteId] });
    }
    queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
  }

  function reloadScheduleQueries() {
    queryClient.invalidateQueries({ queryKey: ['admin-athlete-availability', athleteId] });
    queryClient.invalidateQueries({ queryKey: ['admin-athlete-calendar-events', athleteId] });
    queryClient.invalidateQueries({ queryKey: ['admin-training-blocks', athleteId] });
    queryClient.invalidateQueries({ queryKey: ['admin-athlete', athleteId] });
  }

  function resetOverlayForm() {
    setAddingOverlay(false);
    setEditingOverlayId(null);
    setOverlayName('');
    setOverlayMode('one_off');
    setOverlayDate(null);
    setOverlayWeekday('3');
    setOverlayBlocks(true);
    setOverlayWorkoutId(null);
  }

  function startEditOverlay(event) {
    setAddingOverlay(true);
    setEditingOverlayId(event.id);
    setOverlayName(event.name || '');
    if (event.on_date) {
      setOverlayMode('one_off');
      const dateValue = calendarDateValue(event.on_date);
      setOverlayDate(dateValue ? new Date(`${dateValue}T00:00:00`) : null);
      setOverlayWeekday('3');
    } else {
      setOverlayMode('weekly');
      setOverlayDate(null);
      setOverlayWeekday(String(event.weekday ?? 3));
    }
    setOverlayBlocks(event.blocks_schedule !== false);
    setOverlayWorkoutId(event.workout_id || event.workout?.id || null);
    setConfirmOverlayDeleteId(null);
  }

  function reloadAthlete() {
    queryClient.invalidateQueries({ queryKey: ['admin-athlete', athleteId] });
  }

  function reloadBlocks() {
    queryClient.invalidateQueries({ queryKey: ['admin-training-blocks', athleteId] });
  }

  function onActivateBlock(blockId) {
    setBlockStatusBusy(true);
    axios
      .post(`/training-blocks/${blockId}/activate/`, {})
      .then(() => {
        toast.success('Training block activated');
        reloadScheduleQueries();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setBlockStatusBusy(false));
  }

  function onInactivateBlock(blockId) {
    setBlockStatusBusy(true);
    axios
      .post(`/training-blocks/${blockId}/inactivate/`, {})
      .then(() => {
        toast.success('Training block inactivated');
        setConfirmBlockInactivateId(null);
        reloadScheduleQueries();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setBlockStatusBusy(false));
  }

  function onDeleteTrainingBlock(blockId) {
    setBlockStatusBusy(true);
    axios
      .delete(`/training-blocks/${blockId}/`)
      .then(() => {
        toast.success('Training block deleted');
        setConfirmBlockDeleteId(null);
        reloadScheduleQueries();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setBlockStatusBusy(false));
  }

  function onSave() {
    setSaving(true);
    axios
      .put(`/athletes/${athleteId}/`, {
        first_name: firstName,
        last_name: lastName,
        email,
        sport: sport || null,
        position: position || null,
      })
      .then(({ data }) => {
        toast.success('Athlete saved');
        refreshAthlete(data.response?.[0]);
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSaving(false));
  }

  function onActivate() {
    setStatusBusy(true);
    axios
      .post(`/athletes/${athleteId}/activate/`)
      .then(({ data }) => {
        toast.success('Athlete activated');
        refreshAthlete(data.response?.[0]);
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatusBusy(false));
  }

  function onInactivate() {
    setStatusBusy(true);
    axios
      .post(`/athletes/${athleteId}/inactivate/`)
      .then(({ data }) => {
        toast.success('Athlete inactivated');
        refreshAthlete(data.response?.[0]);
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatusBusy(false));
  }

  function onDelete() {
    setStatusBusy(true);
    axios
      .delete(`/athletes/${athleteId}/`)
      .then(() => {
        toast.success('Athlete deleted');
        queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
        navigate('/');
      })
      .catch((error) => {
        toast.error(toErrorMessage(error));
        setStatusBusy(false);
        setConfirmDelete(false);
      });
  }

  function onAddStat(event) {
    event.preventDefault();
    setStatSaving(true);
    axios
      .post(`/athletes/${athleteId}/stats/`, {
        variety: statVariety,
        value: Number(statValue),
        unit: statUnit,
      })
      .then(() => {
        toast.success('Stat added');
        setAddingStat(false);
        setStatValue('');
        reloadAthlete();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatSaving(false));
  }

  function onDeleteStat(statId) {
    setStatSaving(true);
    axios
      .delete(`/athlete-stats/${statId}/`)
      .then(() => {
        toast.success('Stat deleted');
        setConfirmStatDeleteId(null);
        reloadAthlete();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatSaving(false));
  }

  function onAddMax(event) {
    event.preventDefault();
    if (!maxExerciseId) {
      toast.error('Exercise is required');
      return;
    }
    if (maxValue === '' || Number.isNaN(Number(maxValue))) {
      toast.error('Max value is required');
      return;
    }
    setMaxSaving(true);
    axios
      .post('/exercise-maxes/', {
        athlete_id: athleteId,
        exercise_id: maxExerciseId,
        estimated_max: Math.round(Number(maxValue)),
      })
      .then(() => {
        toast.success('Lift max added');
        setAddingMax(false);
        setMaxExerciseId(null);
        setMaxValue('');
        reloadAthlete();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setMaxSaving(false));
  }

  function onDeleteMax(maxId) {
    setMaxSaving(true);
    axios
      .delete(`/exercise-maxes/${maxId}/`)
      .then(() => {
        toast.success('Lift max deleted');
        setConfirmMaxDeleteId(null);
        reloadAthlete();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setMaxSaving(false));
  }

  function onSaveAvailability() {
    setAvailabilitySaving(true);
    axios
      .put(`/athletes/${athleteId}/availability/`, {
        week_days: weekAvailable.map((available, weekday) => ({
          weekday,
          available,
        })),
      })
      .then(() => {
        toast.success('Availability saved');
        reloadScheduleQueries();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setAvailabilitySaving(false));
  }

  function onSaveOverlay(event) {
    event.preventDefault();
    if (!overlayName.trim()) {
      toast.error('Name is required');
      return;
    }
    const payload = {
      name: overlayName.trim(),
      blocks_schedule: overlayBlocks,
      workout_id: overlayWorkoutId || null,
    };
    if (overlayMode === 'one_off') {
      const onDate = calendarDateValue(overlayDate);
      if (!onDate) {
        toast.error('Date is required for one-off overlays');
        return;
      }
      payload.on_date = onDate;
    } else {
      payload.weekday = Number(overlayWeekday);
    }

    setOverlaySaving(true);
    const request = editingOverlayId
      ? axios.put(`/athlete-calendar-events/${editingOverlayId}/`, payload)
      : axios.post(`/athletes/${athleteId}/calendar-events/`, payload);

    request
      .then(() => {
        toast.success(editingOverlayId ? 'Overlay updated' : 'Overlay added');
        resetOverlayForm();
        reloadScheduleQueries();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setOverlaySaving(false));
  }

  function onDeleteOverlay(eventId) {
    setOverlaySaving(true);
    axios
      .delete(`/athlete-calendar-events/${eventId}/`)
      .then(() => {
        toast.success('Overlay removed');
        setConfirmOverlayDeleteId(null);
        if (editingOverlayId === eventId) resetOverlayForm();
        reloadScheduleQueries();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setOverlaySaving(false));
  }

  function onCreateBlock(event) {
    event.preventDefault();
    const start = calendarDateValue(blockStart);
    const end = calendarDateValue(blockEnd);
    if (!blockName.trim() || !start || !end || blockPattern.length === 0) {
      toast.error('Name, dates, and pattern are required');
      return;
    }
    setBlockSaving(true);
    axios
      .post('/training-blocks/', {
        athlete_id: athleteId,
        name: blockName.trim(),
        start_date: start,
        end_date: end,
        pattern: blockPattern.map((value) => Number(value)),
      })
      .then(({ data }) => {
        const created = data.response?.[0];
        toast.success('Training block created');
        setCreatingBlock(false);
        setBlockName('');
        setBlockStart(null);
        setBlockEnd(null);
        setBlockPattern(DEFAULT_PATTERN);
        reloadAthlete();
        reloadBlocks();
        if (created?.id) {
          navigate(`/athletes/${athleteId}/blocks/${created.id}`);
        }
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setBlockSaving(false));
  }

  function addPatternDay() {
    setBlockPattern((prev) => [...prev, '1']);
  }

  function updatePatternDay(index, value) {
    setBlockPattern((prev) => prev.map((item, i) => (i === index ? value : item)));
  }

  function removePatternDay(index) {
    setBlockPattern((prev) => prev.filter((_, i) => i !== index));
  }

  if (athleteQuery.isLoading) {
    return (
      <Group gap="sm">
        <Loader size="sm" type="dots" />
        <Text c="dimmed">Loading athlete…</Text>
      </Group>
    );
  }

  if (athleteQuery.isError || !athlete) {
    return (
      <Alert variant="light" color="yellow" title="Oops!">
        {athleteQuery.isError
          ? toErrorMessage(athleteQuery.error)
          : 'Athlete not found'}
      </Alert>
    );
  }

  const bodyweightStat = stats.find(
    (stat) => stat.variety_formatted === 'bodyweight' || Number(stat.variety) === 1,
  );
  const profileBits = [
    athlete.sport,
    athlete.position,
    bodyweightStat ? formatStatValue(bodyweightStat) : null,
  ].filter(Boolean);
  const isActive = athlete.status_formatted === 'active';
  const isInactive = athlete.status_formatted === 'inactive';

  return (
    <div>
      <Text size="sm" mb="sm">
        <Link to="/" style={{ color: 'inherit' }}>
          Athletes
        </Link>
        {' / '}
        {athlete.full_name}
      </Text>
      <PageTitle
        title={athlete.full_name}
        subtitle={athlete.email}
        icon={entityIcon.athlete()}
        iconColor={entityColor.athlete}
        actions={
          <Badge variant="light" color={isActive ? 'teal' : 'gray'}>
            {formatLabel(athlete.status_formatted)}
          </Badge>
        }
      />

      {profileBits.length > 0 && (
        <Text mb="lg" c="dimmed">
          {profileBits.join(' · ')}
        </Text>
      )}

      <Accordion
        multiple
        defaultValue={['profile', 'stats', 'trophy-case', 'lift-maxes', 'schedule', 'block']}
        variant="separated"
        radius="md"
      >
        <Accordion.Item value="profile">
          <Accordion.Control>Profile</Accordion.Control>
          <Accordion.Panel>
          <Stack gap="sm" maw={420}>
            <TextInput
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <TextInput
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <TextInput
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextInput
              label="Sport"
              value={sport}
              onChange={(e) => setSport(e.target.value)}
            />
            <TextInput
              label="Position"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            />
            <Group>
              <BasicButton
                text="Save"
                onClick={onSave}
                loading={saving}
                disabled={saving || statusBusy}
                color="var(--brand-brick)"
                variant="filled"
                style={{
                  background: 'var(--brand-brick)',
                  borderColor: 'var(--brand-brick)',
                }}
                primary
              />
              {!isActive && (
                <BasicButton
                  text="Activate"
                  onClick={onActivate}
                  loading={statusBusy}
                  disabled={statusBusy || saving}
                  color="teal"
                  variant="light"
                />
              )}
              {!isInactive && athlete.status_formatted !== 'deleted' && (
                <BasicButton
                  text="Inactivate"
                  onClick={onInactivate}
                  loading={statusBusy}
                  disabled={statusBusy || saving}
                  color="gray"
                  variant="outline"
                />
              )}
            </Group>
            <Group mt="xs">
              {confirmDelete ? (
                <>
                  <Text size="sm" fw={700}>
                    Delete this athlete?
                  </Text>
                  <BasicButton
                    text="Confirm delete"
                    onClick={onDelete}
                    loading={statusBusy}
                    disabled={statusBusy}
                    color="red"
                    variant="filled"
                  />
                  <BasicButton
                    text="Cancel"
                    onClick={() => setConfirmDelete(false)}
                    disabled={statusBusy}
                    color="gray"
                    variant="outline"
                  />
                </>
              ) : (
                <BasicButton
                  text="Delete athlete"
                  onClick={() => setConfirmDelete(true)}
                  disabled={statusBusy || saving}
                  color="red"
                  variant="outline"
                />
              )}
            </Group>
          </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="stats">
          <Accordion.Control>
            Stats{stats.length ? ` (${stats.length})` : ''}
          </Accordion.Control>
          <Accordion.Panel>
          <Group justify="flex-end" mb="sm">
            {!addingStat && (
              <BasicButton
                text="Add stat"
                onClick={() => setAddingStat(true)}
                color={entityColor.athlete}
                variant="light"
              />
            )}
          </Group>

          {addingStat && (
            <Paper
              p="sm"
              mb="md"
              withBorder
              radius="sm"
              component="form"
              onSubmit={onAddStat}
            >
              <Stack gap="sm" maw={420}>
                <Select
                  label="Variety"
                  data={varietyOptions}
                  value={statVariety}
                  onChange={(value) => setStatVariety(value || 'bodyweight')}
                  searchable
                />
                <TextInput
                  label="Value"
                  type="number"
                  value={statValue}
                  onChange={(e) => setStatValue(e.target.value)}
                  required
                />
                <TextInput
                  label="Unit"
                  value={statUnit}
                  onChange={(e) => setStatUnit(e.target.value)}
                />
                <Group>
                  <BasicButton
                    text="Save stat"
                    type="submit"
                    loading={statSaving}
                    disabled={statSaving}
                    color="var(--brand-brick)"
                    variant="filled"
                    style={{
                      background: 'var(--brand-brick)',
                      borderColor: 'var(--brand-brick)',
                    }}
                    primary
                  />
                  <BasicButton
                    text="Cancel"
                    type="button"
                    onClick={() => setAddingStat(false)}
                    disabled={statSaving}
                    color="gray"
                    variant="outline"
                  />
                </Group>
              </Stack>
            </Paper>
          )}

          {stats.length === 0 ? (
            <Text c="dimmed">No current stats yet.</Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
              {stats.map((stat) => (
                <Paper key={stat.id} p="sm" withBorder radius="md">
                  <Text fw={700} tt="capitalize">
                    {formatLabel(stat.variety_formatted)}
                  </Text>
                  <Text size="lg" mt={4}>
                    {formatStatValue(stat)}
                  </Text>
                  <Text size="sm" c="dimmed" mt={4}>
                    {formatStatTakenAt(stat.created_at)}
                  </Text>
                  <Group mt="sm" justify="space-between" align="center">
                    {confirmStatDeleteId === stat.id ? (
                      <Group gap="xs">
                        <BasicButton
                          text="Confirm"
                          onClick={() => onDeleteStat(stat.id)}
                          loading={statSaving}
                          disabled={statSaving}
                          color="red"
                          variant="filled"
                          size="xs"
                        />
                        <BasicButton
                          text="Cancel"
                          onClick={() => setConfirmStatDeleteId(null)}
                          disabled={statSaving}
                          color="gray"
                          variant="outline"
                          size="xs"
                        />
                      </Group>
                    ) : (
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        title="Delete stat"
                        disabled={statSaving}
                        onClick={() => setConfirmStatDeleteId(stat.id)}
                        ml="auto"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Paper>
              ))}
            </SimpleGrid>
          )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="trophy-case">
          <Accordion.Control>
            Trophy Case{awards.length ? ` (${awards.length})` : ''}
          </Accordion.Control>
          <Accordion.Panel>
            {awardsQuery.isLoading ? (
              <Loader size="sm" />
            ) : awards.length === 0 ? (
              <Text c="dimmed">No trophies earned yet.</Text>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                {awards.map((award) => {
                  const achievement = award.achievement || {};
                  const imageUrl =
                    achievement.assets?.find(
                      (asset) =>
                        asset.variety === 1 || asset.variety_formatted === 'image',
                    )?.filename_url || null;
                  return (
                    <Paper key={award.id} p="sm" withBorder radius="md">
                      <Group align="flex-start" gap="sm" wrap="nowrap">
                        {imageUrl ? (
                          <Image
                            src={imageUrl}
                            alt={achievement.name || 'Trophy'}
                            w={56}
                            h={56}
                            radius="sm"
                            fit="cover"
                          />
                        ) : (
                          <Badge variant="light" color="yellow">
                            {achievement.category_formatted || 'trophy'}
                          </Badge>
                        )}
                        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                          <Text fw={700}>{achievement.name || 'Achievement'}</Text>
                          <Text size="sm" c="dimmed">
                            {award.awarded_at
                              ? new Date(award.awarded_at).toLocaleDateString(
                                  undefined,
                                  {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  },
                                )
                              : '—'}
                          </Text>
                        </Stack>
                      </Group>
                    </Paper>
                  );
                })}
              </SimpleGrid>
            )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="lift-maxes">
          <Accordion.Control>
            Lift maxes{liftMaxes.length ? ` (${liftMaxes.length})` : ''}
          </Accordion.Control>
          <Accordion.Panel>
            <Group justify="flex-end" mb="sm">
              {!addingMax && (
                <BasicButton
                  text="Add lift max"
                  onClick={() => setAddingMax(true)}
                  color={entityColor.athlete}
                  variant="light"
                />
              )}
            </Group>

            {addingMax && (
              <Paper
                p="sm"
                mb="md"
                withBorder
                radius="sm"
                component="form"
                onSubmit={onAddMax}
              >
                <Stack gap="sm" maw={420}>
                  <Select
                    label="Exercise"
                    data={exerciseOptions}
                    value={maxExerciseId}
                    onChange={(value) => setMaxExerciseId(value)}
                    searchable
                    nothingFoundMessage="No exercises"
                    required
                  />
                  <TextInput
                    label="Estimated max (lb)"
                    type="number"
                    step="1"
                    value={maxValue}
                    onChange={(e) => setMaxValue(e.target.value)}
                    required
                  />
                  <Group>
                    <BasicButton
                      text="Save max"
                      type="submit"
                      loading={maxSaving}
                      disabled={maxSaving}
                      color="var(--brand-brick)"
                      variant="filled"
                      style={{
                        background: 'var(--brand-brick)',
                        borderColor: 'var(--brand-brick)',
                      }}
                      primary
                    />
                    <BasicButton
                      text="Cancel"
                      type="button"
                      onClick={() => setAddingMax(false)}
                      disabled={maxSaving}
                      color="gray"
                      variant="outline"
                    />
                  </Group>
                </Stack>
              </Paper>
            )}

            {liftMaxes.length === 0 ? (
              <Text c="dimmed">No current lift maxes yet.</Text>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                {liftMaxes.map((maxRow) => (
                  <Paper key={maxRow.id} p="sm" withBorder radius="md">
                    <Text fw={700}>{maxRow.exercise?.name || 'Exercise'}</Text>
                    <Text size="lg" mt={4}>
                      {maxRow.estimated_max != null
                        ? String(Math.round(Number(maxRow.estimated_max)))
                        : '—'}
                    </Text>
                    <Text size="sm" c="dimmed" mt={4}>
                      {formatStatTakenAt(maxRow.created_at)}
                    </Text>
                    <Group mt="sm" justify="space-between" align="center">
                      {confirmMaxDeleteId === maxRow.id ? (
                        <Group gap="xs">
                          <BasicButton
                            text="Confirm"
                            onClick={() => onDeleteMax(maxRow.id)}
                            loading={maxSaving}
                            disabled={maxSaving}
                            color="red"
                            variant="filled"
                            size="xs"
                          />
                          <BasicButton
                            text="Cancel"
                            onClick={() => setConfirmMaxDeleteId(null)}
                            disabled={maxSaving}
                            color="gray"
                            variant="outline"
                            size="xs"
                          />
                        </Group>
                      ) : (
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          title="Delete lift max"
                          disabled={maxSaving}
                          onClick={() => setConfirmMaxDeleteId(maxRow.id)}
                          ml="auto"
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Paper>
                ))}
              </SimpleGrid>
            )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="schedule">
          <Accordion.Control>
            Schedule
            {overlays.length ? ` (${overlays.length} overlays)` : ''}
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              <Text size="xs" c="dimmed">
                Unavailable weekdays and blocking overlays keep training-block slots off those
                days. Non-blocking overlays can stack on top of a TB day (for example Faceoff
                plus Peloton). Gym-wide holidays live on{' '}
                <Link to="/gym-closures">Gym closures</Link>
                .
              </Text>

              <Paper p="sm" withBorder radius="sm">
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Text fw={700}>Weekly availability</Text>
                    <BasicButton
                      text={availabilitySaving ? 'Saving…' : 'Save week'}
                      onClick={onSaveAvailability}
                      loading={availabilitySaving}
                      disabled={availabilitySaving || availabilityQuery.isLoading}
                      color="var(--brand-brick)"
                      variant="filled"
                      style={{
                        background: 'var(--brand-brick)',
                        borderColor: 'var(--brand-brick)',
                      }}
                      primary
                    />
                  </Group>
                  {availabilityQuery.isLoading ? (
                    <Group gap="sm">
                      <Loader size="sm" type="dots" />
                      <Text size="sm" c="dimmed">
                        Loading availability…
                      </Text>
                    </Group>
                  ) : (
                    <SimpleGrid cols={{ base: 2, sm: 4, md: 7 }} spacing="sm">
                      {WEEKDAY_SHORT.map((label, weekday) => (
                        <Paper key={label} p="xs" withBorder radius="sm">
                          <Stack gap={6} align="center">
                            <Text size="sm" fw={600}>
                              {label}
                            </Text>
                            <Switch
                              label={weekAvailable[weekday] ? 'Open' : 'Blackout'}
                              checked={weekAvailable[weekday]}
                              onChange={(event) => {
                                const checked = event.currentTarget.checked;
                                setWeekAvailable((prev) =>
                                  prev.map((value, index) =>
                                    index === weekday ? checked : value,
                                  ),
                                );
                              }}
                            />
                          </Stack>
                        </Paper>
                      ))}
                    </SimpleGrid>
                  )}
                </Stack>
              </Paper>

              <Paper p="sm" withBorder radius="sm">
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Text fw={700}>Calendar overlays</Text>
                    {!addingOverlay && (
                      <BasicButton
                        text="Add overlay"
                        onClick={() => {
                          resetOverlayForm();
                          setAddingOverlay(true);
                        }}
                        color={entityColor.athlete}
                        variant="light"
                      />
                    )}
                  </Group>

                  {addingOverlay && (
                    <Paper
                      p="sm"
                      withBorder
                      radius="sm"
                      component="form"
                      onSubmit={onSaveOverlay}
                    >
                      <Stack gap="sm" maw={480}>
                        <Text fw={600}>
                          {editingOverlayId ? 'Edit overlay' : 'New overlay'}
                        </Text>
                        <TextInput
                          label="Name"
                          value={overlayName}
                          onChange={(e) => setOverlayName(e.target.value)}
                          required
                        />
                        <SegmentedControl
                          value={overlayMode}
                          onChange={setOverlayMode}
                          data={[
                            { value: 'one_off', label: 'One-off' },
                            { value: 'weekly', label: 'Weekly' },
                          ]}
                        />
                        {overlayMode === 'one_off' ? (
                          <DatePickerInput
                            label="Date"
                            value={overlayDate}
                            onChange={setOverlayDate}
                            required
                          />
                        ) : (
                          <Select
                            label="Weekday"
                            data={WEEKDAY_OPTIONS}
                            value={overlayWeekday}
                            onChange={(value) => setOverlayWeekday(value || '3')}
                          />
                        )}
                        <Switch
                          label="Blocks training schedule"
                          description="When on, TB layout and pez skip this day"
                          checked={overlayBlocks}
                          onChange={(event) =>
                            setOverlayBlocks(event.currentTarget.checked)
                          }
                        />
                        <Select
                          label="Workout (optional)"
                          data={workoutOptions}
                          value={overlayWorkoutId}
                          onChange={setOverlayWorkoutId}
                          searchable
                          clearable
                          placeholder="None"
                        />
                        <Group>
                          <BasicButton
                            text={
                              overlaySaving
                                ? 'Saving…'
                                : editingOverlayId
                                  ? 'Update overlay'
                                  : 'Save overlay'
                            }
                            type="submit"
                            loading={overlaySaving}
                            disabled={overlaySaving}
                            color="var(--brand-brick)"
                            variant="filled"
                            style={{
                              background: 'var(--brand-brick)',
                              borderColor: 'var(--brand-brick)',
                            }}
                            primary
                          />
                          <BasicButton
                            text="Cancel"
                            type="button"
                            onClick={resetOverlayForm}
                            disabled={overlaySaving}
                            color="gray"
                            variant="outline"
                          />
                        </Group>
                      </Stack>
                    </Paper>
                  )}

                  {overlaysQuery.isLoading ? (
                    <Group gap="sm">
                      <Loader size="sm" type="dots" />
                      <Text size="sm" c="dimmed">
                        Loading overlays…
                      </Text>
                    </Group>
                  ) : overlays.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      No overlays yet. Add a class night or stacked session here.
                    </Text>
                  ) : (
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Name</Table.Th>
                          <Table.Th>When</Table.Th>
                          <Table.Th>Blocks</Table.Th>
                          <Table.Th>Workout</Table.Th>
                          <Table.Th />
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {overlays.map((event) => (
                          <Table.Tr key={event.id}>
                            <Table.Td>{event.name}</Table.Td>
                            <Table.Td>{formatOverlayWhen(event)}</Table.Td>
                            <Table.Td>
                              <Badge
                                size="sm"
                                variant="light"
                                color={event.blocks_schedule ? 'red' : 'teal'}
                              >
                                {event.blocks_schedule ? 'Blocks' : 'Stacks'}
                              </Badge>
                            </Table.Td>
                            <Table.Td>{event.workout?.name || '—'}</Table.Td>
                            <Table.Td>
                              <Group gap="xs" justify="flex-end">
                                <BasicButton
                                  text="Edit"
                                  size="xs"
                                  variant="light"
                                  color="gray"
                                  onClick={() => startEditOverlay(event)}
                                  disabled={overlaySaving}
                                />
                                {confirmOverlayDeleteId === event.id ? (
                                  <Group gap="xs">
                                    <BasicButton
                                      text="Confirm"
                                      size="xs"
                                      color="red"
                                      variant="filled"
                                      onClick={() => onDeleteOverlay(event.id)}
                                      loading={overlaySaving}
                                      disabled={overlaySaving}
                                    />
                                    <BasicButton
                                      text="Cancel"
                                      size="xs"
                                      color="gray"
                                      variant="outline"
                                      onClick={() => setConfirmOverlayDeleteId(null)}
                                      disabled={overlaySaving}
                                    />
                                  </Group>
                                ) : (
                                  <ActionIcon
                                    color="red"
                                    variant="subtle"
                                    title="Delete overlay"
                                    disabled={overlaySaving}
                                    onClick={() => setConfirmOverlayDeleteId(event.id)}
                                  >
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                )}
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="block">
          <Accordion.Control>Training blocks</Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              <Text size="xs" c="dimmed">
                Buckets are by date window. Pause or delete a block here, or open it to manage
                workouts and layout. Focus slots below follow today&apos;s kiosk focus block.
              </Text>

              {blocksQuery.isLoading ? (
                <Group gap="sm">
                  <Loader size="sm" type="dots" />
                  <Text size="sm" c="dimmed">
                    Loading blocks…
                  </Text>
                </Group>
              ) : (
                <Stack gap="sm">
                  {[
                    { key: 'current', title: 'Current', items: currentBlocks },
                    { key: 'upcoming', title: 'Upcoming', items: upcomingBlocks },
                    { key: 'historical', title: 'Historical', items: historicalBlocks },
                  ].map((bucket) => (
                    <Stack key={bucket.key} gap={6}>
                      <Text size="sm" fw={700}>
                        {bucket.title}
                        {bucket.items.length ? ` (${bucket.items.length})` : ''}
                      </Text>
                      {bucket.items.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          None
                        </Text>
                      ) : (
                        bucket.items.map((block) => {
                          const isDeleted = block.status_formatted === 'deleted';
                          const isActive = block.status_formatted === 'active';
                          const isInactive = block.status_formatted === 'inactive';
                          const confirmingInactivate =
                            confirmBlockInactivateId === block.id;
                          const confirmingDelete = confirmBlockDeleteId === block.id;
                          return (
                            <Paper key={block.id} p="sm" withBorder radius="sm">
                              <Group
                                justify="space-between"
                                align="flex-start"
                                wrap="nowrap"
                                gap="sm"
                              >
                                <Link
                                  to={`/athletes/${athleteId}/blocks/${block.id}`}
                                  style={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <Stack gap={2}>
                                    <Text fw={700}>{block.name}</Text>
                                    <Text size="sm" c="dimmed">
                                      {formatDate(block.start_date)} –{' '}
                                      {formatDate(block.end_date)}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {block.workout_count || 0} workouts ·{' '}
                                      {block.slot_count || 0} slots ·{' '}
                                      {block.pattern_day_count || 0} pattern days
                                    </Text>
                                  </Stack>
                                </Link>
                                <Stack gap="xs" align="flex-end">
                                  <Badge
                                    size="sm"
                                    variant="light"
                                    color={blockStatusColor(block.status_formatted)}
                                  >
                                    {formatLabel(block.status_formatted)}
                                  </Badge>
                                  {!isDeleted &&
                                  !confirmingInactivate &&
                                  !confirmingDelete ? (
                                    <Group gap="xs">
                                      {!isActive ? (
                                        <BasicButton
                                          text="Activate"
                                          size="xs"
                                          color="teal"
                                          variant="light"
                                          loading={blockStatusBusy}
                                          disabled={blockStatusBusy}
                                          onClick={() => onActivateBlock(block.id)}
                                        />
                                      ) : null}
                                      {!isInactive ? (
                                        <BasicButton
                                          text="Inactivate"
                                          size="xs"
                                          color="gray"
                                          variant="light"
                                          disabled={blockStatusBusy}
                                          onClick={() => {
                                            setConfirmBlockDeleteId(null);
                                            setConfirmBlockInactivateId(block.id);
                                          }}
                                        />
                                      ) : null}
                                      <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        title="Delete training block"
                                        disabled={blockStatusBusy}
                                        onClick={() => {
                                          setConfirmBlockInactivateId(null);
                                          setConfirmBlockDeleteId(block.id);
                                        }}
                                      >
                                        <IconTrash size={16} />
                                      </ActionIcon>
                                    </Group>
                                  ) : null}
                                  {confirmingInactivate ? (
                                    <Stack gap={4} maw={240}>
                                      <Text size="xs" c="dimmed">
                                        Pause this block? Pending sessions stay, but leave
                                        the kiosk and calendar until you activate again. Any
                                        open session will be abandoned.
                                      </Text>
                                      <Group gap="xs">
                                        <BasicButton
                                          text="Confirm"
                                          size="xs"
                                          color="gray"
                                          variant="filled"
                                          loading={blockStatusBusy}
                                          disabled={blockStatusBusy}
                                          onClick={() => onInactivateBlock(block.id)}
                                        />
                                        <BasicButton
                                          text="Cancel"
                                          size="xs"
                                          color="gray"
                                          variant="outline"
                                          disabled={blockStatusBusy}
                                          onClick={() =>
                                            setConfirmBlockInactivateId(null)
                                          }
                                        />
                                      </Group>
                                    </Stack>
                                  ) : null}
                                  {confirmingDelete ? (
                                    <Stack gap={4} maw={240}>
                                      <Text size="xs" c="dimmed">
                                        Delete this block? Remaining pending sessions are
                                        removed. History stays. Any open session will be
                                        abandoned.
                                      </Text>
                                      <Group gap="xs">
                                        <BasicButton
                                          text="Confirm"
                                          size="xs"
                                          color="red"
                                          variant="filled"
                                          loading={blockStatusBusy}
                                          disabled={blockStatusBusy}
                                          onClick={() =>
                                            onDeleteTrainingBlock(block.id)
                                          }
                                        />
                                        <BasicButton
                                          text="Cancel"
                                          size="xs"
                                          color="gray"
                                          variant="outline"
                                          disabled={blockStatusBusy}
                                          onClick={() => setConfirmBlockDeleteId(null)}
                                        />
                                      </Group>
                                    </Stack>
                                  ) : null}
                                </Stack>
                              </Group>
                            </Paper>
                          );
                        })
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}

              {!creatingBlock ? (
                <BasicButton text="Create block" onClick={() => setCreatingBlock(true)} />
              ) : (
                <Paper component="form" onSubmit={onCreateBlock} p="sm" withBorder radius="sm">
                  <Stack gap="sm">
                    <Text fw={700}>Create training block</Text>
                    <Text size="xs" c="dimmed">
                      Set name, dates, and pattern. You&apos;ll open the block detail next to add
                      workouts and layout slots.
                    </Text>
                    <TextInput
                      label="Name"
                      value={blockName}
                      onChange={(event) => setBlockName(event.currentTarget.value)}
                      required
                    />
                    <Group grow>
                      <DatePickerInput
                        label="Start"
                        value={blockStart}
                        onChange={setBlockStart}
                        required
                      />
                      <DatePickerInput
                        label="End"
                        value={blockEnd}
                        onChange={setBlockEnd}
                        required
                      />
                    </Group>
                    <Stack gap="xs">
                      <Text size="sm" fw={600}>
                        Pattern
                      </Text>
                      {blockPattern.map((dayKind, index) => (
                        <Group key={`pattern-${index}`} gap="xs" wrap="nowrap">
                          <Select
                            data={DAY_KIND_OPTIONS}
                            value={dayKind}
                            onChange={(value) => updatePatternDay(index, value || '1')}
                            style={{ flex: 1 }}
                          />
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => removePatternDay(index)}
                            disabled={blockPattern.length <= 1}
                            aria-label="Remove pattern day"
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      ))}
                      <BasicButton text="Add pattern day" onClick={addPatternDay} />
                    </Stack>
                    <Group>
                      <BasicButton
                        text={blockSaving ? 'Saving…' : 'Save block'}
                        type="submit"
                        disabled={blockSaving}
                      />
                      <BasicButton
                        text="Cancel"
                        onClick={() => setCreatingBlock(false)}
                        disabled={blockSaving}
                      />
                    </Group>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="slots">
          <Accordion.Control>
            Focus slots{slots.length ? ` (${slots.length})` : ''}
            {focusBlock?.name ? ` · ${focusBlock.name}` : ''}
          </Accordion.Control>
          <Accordion.Panel>
          <Text size="xs" c="dimmed" mb="sm">
            Today&apos;s kiosk focus block (not necessarily the block selected above).
          </Text>
          {slots.length === 0 ? (
            <Text c="dimmed">No slots on this block.</Text>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Kind</Table.Th>
                  <Table.Th>Workout</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Complete</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {slots.map((slot) => {
                  const focused = focusSlot && slot.id === focusSlot.id;
                  return (
                    <Table.Tr
                      key={slot.id}
                      bg={focused ? 'var(--mantine-color-yellow-0)' : undefined}
                    >
                      <Table.Td>{formatDate(slot.recommended_on)}</Table.Td>
                      <Table.Td>{formatLabel(slot.day_kind_formatted)}</Table.Td>
                      <Table.Td>
                        {slot.workout?.name || '—'}
                        {focused ? (
                          <Text span size="xs" c="dimmed" ml={6}>
                            focus
                          </Text>
                        ) : null}
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          variant="light"
                          color={slotStatusColor(slot.status_formatted)}
                        >
                          {formatLabel(slot.status_formatted)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {slot.percent_complete != null ? `${slot.percent_complete}%` : '—'}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="focus">
          <Accordion.Control>
            Focus workout
            {focusSlot?.workout?.name ? ` · ${focusSlot.workout.name}` : ''}
          </Accordion.Control>
          <Accordion.Panel>
          {!focusSlot || !focusSlot.workout ? (
            <Text c="dimmed">No focus slot or workout outline.</Text>
          ) : (
            <Stack gap="md">
              <div>
                <Text fw={700}>{focusSlot.workout.name}</Text>
                {focusSlot.workout.notes ? (
                  <Text size="sm" c="dimmed">
                    {focusSlot.workout.notes}
                  </Text>
                ) : null}
              </div>
              {exercises.length === 0 ? (
                <Text c="dimmed">No exercises on this workout.</Text>
              ) : (
                exercises.map((workoutExercise) => {
                  const exercise = workoutExercise.exercise || {};
                  const cues = exercise.coaching_cues || [];
                  const sets = workoutExercise.sets || [];
                  return (
                    <Paper
                      key={workoutExercise.id}
                      p="sm"
                      withBorder
                      radius="sm"
                      bg="var(--mantine-color-gray-0)"
                    >
                      <Text fw={700}>
                        {(workoutExercise.position ?? 0) + 1}. {exercise.name || 'Exercise'}
                      </Text>
                      {sets.length > 0 && (
                        <Stack gap={2} mt={6}>
                          {sets.map((set) => (
                            <Text key={set.id} size="sm">
                              Set {(set.position ?? 0) + 1}: {formatSet(set)}
                            </Text>
                          ))}
                        </Stack>
                      )}
                      {cues.length > 0 && (
                        <Text size="sm" c="dimmed" mt="xs">
                          Cues: {cues.join(' · ')}
                        </Text>
                      )}
                    </Paper>
                  );
                })
              )}
            </Stack>
          )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </div>
  );
}
